import React, { useState, useEffect, useCallback, useRef } from "react";
import { useParams, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { trackingAPI, reviewAPI } from "@/lib/api";
import { useAuth } from "@/App";
import { TrackingMap, geocodeAddress } from "@/components/DeliveryMap";
import { toast } from "sonner";
import { 
  Package, 
  ArrowLeft,
  Loader2,
  Timer,
  Navigation,
  User
} from "lucide-react";
import { formatDistanceToNow } from "date-fns";

// Import modular components
import {
  TrackingProgress,
  TrackingActivityLog,
  DeliveryReviewForm,
  TrackingHeader,
  RouteSummary,
  DeliveredBanner
} from "@/components/tracking";

const STATUS_STEPS = TrackingProgress.STATUS_STEPS;

export default function TrackingPage() {
  const { deliveryId } = useParams();
  const { user } = useAuth();
  const [tracking, setTracking] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(false);
  
  // Review state
  const [rating, setRating] = useState(0);
  const [reviewComment, setReviewComment] = useState("");
  const [submittingReview, setSubmittingReview] = useState(false);
  const [reviewSubmitted, setReviewSubmitted] = useState(false);
  
  // Map locations
  const [pickupCoords, setPickupCoords] = useState(null);
  const [dropCoords, setDropCoords] = useState(null);
  const [partnerCoords, setPartnerCoords] = useState(null);
  
  // Refs
  const websocketRef = useRef(null);
  const geocodedRef = useRef({ pickup: false, drop: false });

  // Tracking URL for sharing
  const trackingUrl = `${window.location.origin}/track/${deliveryId}`;

  // Share tracking link
  const handleShare = useCallback(async () => {
    const shareData = {
      title: `Track Delivery ${deliveryId}`,
      text: `Track your Setuxpress delivery`,
      url: trackingUrl
    };

    if (navigator.share && navigator.canShare?.(shareData)) {
      try {
        await navigator.share(shareData);
        toast.success("Shared successfully!");
        return;
      } catch (err) {
        if (err.name === 'AbortError') return;
      }
    }

    // Fallback to clipboard
    try {
      await navigator.clipboard.writeText(trackingUrl);
      setCopied(true);
      toast.success("Tracking link copied!");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      try {
        const textArea = document.createElement('textarea');
        textArea.value = trackingUrl;
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
        setCopied(true);
        toast.success("Tracking link copied!");
        setTimeout(() => setCopied(false), 2000);
      } catch {
        toast.error("Failed to copy link");
      }
    }
  }, [deliveryId, trackingUrl]);

  // Fetch tracking data
  const fetchTracking = useCallback(async () => {
    try {
      const response = await trackingAPI.getInfo(deliveryId);
      setTracking(response.data);
      setError(null);
      
      if (response.data.partner_location) {
        setPartnerCoords({
          lat: response.data.partner_location.latitude,
          lng: response.data.partner_location.longitude
        });
      }
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to load tracking info");
    } finally {
      setLoading(false);
    }
  }, [deliveryId]);

  // Initial load
  useEffect(() => {
    fetchTracking();
  }, [fetchTracking]);

  // Geocode addresses
  useEffect(() => {
    if (!tracking) return;
    
    const geocode = async () => {
      if (tracking.pickup_address && !geocodedRef.current.pickup) {
        geocodedRef.current.pickup = true;
        const coords = await geocodeAddress(tracking.pickup_address + ", India");
        if (coords) setPickupCoords(coords);
      }
      
      if (tracking.drop_address && !geocodedRef.current.drop) {
        geocodedRef.current.drop = true;
        const coords = await geocodeAddress(tracking.drop_address + ", India");
        if (coords) setDropCoords(coords);
      }
    };
    
    const timer = setTimeout(geocode, 1500);
    return () => clearTimeout(timer);
  }, [tracking]);

  // Poll for updates
  useEffect(() => {
    if (!tracking || tracking.status === "delivered" || tracking.status === "cancelled") {
      return;
    }
    
    const interval = setInterval(fetchTracking, 10000);
    return () => clearInterval(interval);
  }, [fetchTracking, tracking]);

  // WebSocket for real-time updates
  useEffect(() => {
    if (!user?.user_id) return;

    const protocol = window.location.protocol === "https:" ? "wss:" : "ws:";
    const wsUrl = `${protocol}//${window.location.host}/ws/${user.user_id}`;
    
    try {
      const ws = new WebSocket(wsUrl);
      websocketRef.current = ws;
      
      ws.onmessage = (event) => {
        if (event.data === "pong") return;
        
        try {
          const data = JSON.parse(event.data);
          
          if (data.type === "tracking_update" && data.delivery_id === deliveryId) {
            setTracking((prev) => prev ? {
              ...prev,
              status: data.status,
              tracking_updates: [...(prev.tracking_updates || []), data.tracking_update],
              last_updated: data.timestamp,
            } : prev);
          }
          
          if (data.type === "location_update" && data.delivery_id === deliveryId) {
            setPartnerCoords({ lat: data.latitude, lng: data.longitude });
          }
        } catch (parseErr) {
          if (process.env.NODE_ENV === 'development') {
            console.debug('WebSocket parse error:', parseErr);
          }
        }
      };
    } catch (wsErr) {
      if (process.env.NODE_ENV === 'development') {
        console.debug('WebSocket connection failed:', wsErr);
      }
    }
    
    return () => {
      websocketRef.current?.close();
      websocketRef.current = null;
    };
  }, [user?.user_id, deliveryId]);

  const getCurrentStepIndex = useCallback(() => {
    if (!tracking) return 0;
    const index = STATUS_STEPS.findIndex((s) => s.key === tracking.status);
    return index >= 0 ? index : 0;
  }, [tracking]);

  const handleSubmitReview = async () => {
    if (rating === 0) {
      toast.error("Please select a rating");
      return;
    }
    
    setSubmittingReview(true);
    try {
      await reviewAPI.createDeliveryReview({
        service_type: "local_delivery",
        reference_id: deliveryId,
        rating: rating,
        comment: reviewComment.trim() || undefined
      });
      toast.success("Thank you for your feedback!");
      setReviewSubmitted(true);
    } catch (error) {
      if (error.response?.status === 400 && error.response?.data?.detail?.includes("already")) {
        toast.info("You've already reviewed this delivery");
        setReviewSubmitted(true);
      } else {
        toast.error(error.response?.data?.detail || "Failed to submit review");
      }
    } finally {
      setSubmittingReview(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <div className="text-center">
          <Package className="w-16 h-16 mx-auto text-muted-foreground/30 mb-4" />
          <h1 className="text-xl font-bold mb-2">Tracking Not Found</h1>
          <p className="text-muted-foreground mb-4">{error}</p>
          <Link to="/"><Button className="rounded-none">Go Home</Button></Link>
        </div>
      </div>
    );
  }

  const currentStep = getCurrentStepIndex();
  const isActive = tracking?.status !== "delivered" && tracking?.status !== "cancelled";
  const hasPartnerAssigned = tracking?.partner_id;

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b border-border sticky top-0 bg-background z-50">
        <div className="container mx-auto px-4 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <img src="/logo-new.png" alt="Setuxpress" className="h-10" />
            <span className="font-black text-xl tracking-tight">setuxpress</span>
          </Link>
          <Link to={user?.user_type === "partner" ? "/partner" : "/dashboard"}>
            <Button variant="ghost" className="gap-2">
              <ArrowLeft className="w-4 h-4" />
              Back
            </Button>
          </Link>
        </div>
      </header>

      {/* Live Map */}
      <div className="relative">
        <TrackingMap
          pickupLocation={pickupCoords}
          dropLocation={dropCoords}
          driverLocation={partnerCoords}
          status={tracking?.status}
          height="350px"
          className="w-full"
        />
        
        {/* ETA Card */}
        {tracking?.eta && isActive && (
          <div className="absolute bottom-4 left-4 right-4 md:left-auto md:right-4 md:w-80 bg-background border-2 border-primary shadow-lg p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-muted-foreground">Arriving in</p>
                <p className="text-2xl font-black text-primary">
                  {tracking.eta.eta_display || "Calculating..."}
                </p>
              </div>
              <div className="w-12 h-12 bg-primary/20 rounded-full flex items-center justify-center">
                <Timer className="w-6 h-6 text-primary" />
              </div>
            </div>
            {tracking.eta.distance_remaining_km && (
              <p className="text-xs text-muted-foreground mt-2 flex items-center gap-1">
                <Navigation className="w-3 h-3" />
                {tracking.eta.distance_remaining_km} km away
              </p>
            )}
          </div>
        )}
        
        {/* Partner Card - Show contact details when assigned */}
        {hasPartnerAssigned && isActive && (
          <div className="absolute top-4 left-4 bg-background border shadow-lg p-4 max-w-xs">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-orange-500 rounded-full flex items-center justify-center">
                <User className="w-6 h-6 text-white" />
              </div>
              <div className="flex-1">
                <p className="font-semibold">{tracking?.partner_name || "Delivery Partner"}</p>
                <p className="text-xs text-muted-foreground">
                  {tracking?.vehicle_type ? tracking.vehicle_type.charAt(0).toUpperCase() + tracking.vehicle_type.slice(1) : "On the way"}
                </p>
              </div>
              {partnerCoords && <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />}
            </div>
            
            {/* Partner Contact */}
            {tracking?.partner_phone && (
              <div className="mt-3 pt-3 border-t border-border">
                <a 
                  href={`tel:${tracking.partner_phone}`}
                  className="flex items-center justify-center gap-2 w-full bg-primary text-primary-foreground py-2 px-4 text-sm font-medium hover:bg-primary/90 transition-colors"
                  data-testid="call-partner-btn"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"/>
                  </svg>
                  Call {tracking.partner_phone}
                </a>
              </div>
            )}
            
            {/* ETA in partner card */}
            {tracking?.eta?.eta_display && (
              <div className="mt-2 text-xs text-center text-muted-foreground">
                Arriving in <span className="text-primary font-semibold">{tracking.eta.eta_display}</span>
              </div>
            )}
          </div>
        )}
      </div>

      <div className="container mx-auto px-4 py-6 max-w-3xl">
        {/* Header with Order ID and Actions */}
        <TrackingHeader
          deliveryId={deliveryId}
          copied={copied}
          onShare={handleShare}
          onRefresh={fetchTracking}
        />

        {/* Route Summary */}
        <RouteSummary tracking={tracking} />

        {/* Delivered Banner */}
        {tracking?.status === "delivered" && <DeliveredBanner />}

        {/* Status Progress */}
        <TrackingProgress currentStep={currentStep} isActive={isActive} />

        {/* Activity Log */}
        <TrackingActivityLog updates={tracking?.tracking_updates} />

        {/* Review Section - Only show for delivered orders */}
        {tracking?.status === "delivered" && (
          <div className="mt-6">
            <DeliveryReviewForm
              rating={rating}
              setRating={setRating}
              comment={reviewComment}
              setComment={setReviewComment}
              onSubmit={handleSubmitReview}
              submitting={submittingReview}
              submitted={reviewSubmitted}
            />
          </div>
        )}

        {tracking?.last_updated && (
          <p className="text-center text-xs text-muted-foreground mt-4">
            Updated {formatDistanceToNow(new Date(tracking.last_updated), { addSuffix: true })}
          </p>
        )}
      </div>
    </div>
  );
}
